import axios from 'axios';
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogActions, Toolbar, Typography, CircularProgress, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import DownloadButton from '../../components/download_button';
import Config from '../../config';
import Papa from 'papaparse';
import FileSaverBase from 'filesaver.js-npm';
import Image from '../../components/image';
import isEqual from 'lodash.isequal';
import PDFMerger from 'pdf-merger-js/browser';
import ExtendedSelectBase from '../../components/select';

// select.js default-exports a forwardRef component with untyped props; the
// inferred type rejects displayEmpty/style/onChange/etc. Relax to `any`
// (matches pages/files/export.tsx's ExtendedSelectAny cast).
const ExtendedSelect: any = ExtendedSelectBase;
// filesaver.js-npm ships no type declarations (matches pages/inventory/v1/catalog_export.tsx's cast).
const FileSaver: any = FileSaverBase;

const lbKey = 'Identifier.LogicbrokerKey';
const trackingNumber = 'ShipmentInfo.TrackingNumber';
const quantity = 'OrderLine.Quantity';
const description = 'OrderLine.Description';
const classCode = 'ShipmentInfo.ClassCode';
const senderClassCode = 'ShipmentInfo.SenderClassCode';
const supplierSku = 'OrderLine.ItemIdentifier.SupplierSKU';
const partnerSku = 'OrderLine.ItemIdentifier.PartnerSKU';
const shipToState = 'ShipToAddress.State';
const arrivalType = 'ExtendedAttribute[Name=Arrival Type].Value';
const requestedDelivery = 'ExtendedAttribute[Name=Requested Delivery].Value';
const requestedShipDate = 'RequestedShipDate';
const columns = [lbKey, 'PartnerPO', 'OrderNumber', requestedShipDate, classCode, senderClassCode, trackingNumber, supplierSku, partnerSku, quantity, description, shipToState, arrivalType, requestedDelivery];

interface BulkPickState {
    loaded: boolean;
    fileName: string;
    showModal: boolean;
    showError: boolean;
    error: string;
    orders: any[];
    trackedOrderCount: number;
    filterSku: string[];
    filterPartnerSku: string[];
    filterShipDate: string[];
    sort: string[];
    skus: string[];
    shipDates: string[];
    partnerSkus: string[];
    loadedOrders?: boolean;
    imageUrl?: string | null;
}

const BulkPick = (props: any) => {
    const [state, setState] = useState<BulkPickState>({loaded: false, fileName: '', showModal: false, showError: false, error: '', orders: [], trackedOrderCount: 0,
    filterSku: [], filterPartnerSku: [], filterShipDate: [], sort: [supplierSku, senderClassCode, shipToState],
    skus: [], shipDates: [], partnerSkus: []});

    const loadOrders = async () => {
        setState(prev => ({...prev, loaded: false}));
        let orderStatus = 500;
        let req = {
            Columns: columns.map(r => ({SourceField: r, TargetField: r})),
            Delimiter: ',',
            FileType: 'csv',
            Filter: {
                advanced: `StatusCode eq ${orderStatus}`
            }
        };
        let exportLink = (await axios.post(Config.api + '/api/v2/orders/export', req)).data.Body;
        let csv = (await axios.get(exportLink)).data;
        let orders: any[] = Papa.parse(csv, {header: true, skipEmptyLines: true}).data;
        let skus = orders.map(r => r[supplierSku]);
        skus = skus.filter((r, i) => skus.indexOf(r) === i).sort();
        let partnerSkus = orders.map(r => r[partnerSku]);
        partnerSkus = partnerSkus.filter((r, i) => !(!r) && partnerSkus.indexOf(r) === i).sort();
        let shipDates = orders.map(r => r[requestedShipDate]);
        shipDates = shipDates.filter((r, i) => !(!r) && shipDates.indexOf(r) === i).sort();
        let orderIds = getUniqueIds(orders);
        let imageUrl = null;
        if(orderIds.length > 0){
            imageUrl = (await axios.get(Config.api + `/api/v1/orders/${orderIds[0]}/picklist?filetype=png`)).data.Body;
        }
        setState(prev => ({...prev, showModal: true, orders, loadedOrders: orders.length > 0,
            imageUrl, fileName: new Date().getTime().toString() + '-picklist.pdf', shipDates, skus, partnerSkus}));
    };
    const isFiltered = () => {
        return state.filterSku.length > 0 || state.filterShipDate.length > 0 || state.filterPartnerSku.length > 0;
    };
    const filterOrders = (orders: any[]) => {
        if(state.filterSku.length > 0){
            orders = orders.filter(r => state.filterSku.filter(x => r[supplierSku] === x).length > 0);
        }
        if(state.filterPartnerSku.length > 0){
            orders = orders.filter(r => state.filterSku.filter(x => r[partnerSku] === x).length > 0);
        }
        if(state.filterShipDate.length > 0){
            orders = orders.filter(r => state.filterShipDate.filter(x => r[requestedShipDate] === x).length > 0);
        }
        return orders;
    };
    const getUniqueIds = (orders: any[]) => {
        orders = filterOrders(orders);
        let orderIds = orders.map(r => r[lbKey]);
        orderIds = orderIds.filter((r, i) => orderIds.indexOf(r) === i);
        return orderIds;
    };
    const getUniqueIdsWithTracking = (orders: any[]) => {
        orders = filterOrders(orders);
        orders = orders.filter(r => !(!r[trackingNumber]));
        return getUniqueIds(orders);
    };
    const downloadPickList = async (updateStatus: boolean, setProgress?: (progress: number) => void) => {
        // sort
        let sorted = sort(state.orders, state.sort);
        let orderIds = getUniqueIds(sorted);
        let trackedOrderIds = getUniqueIdsWithTracking(sorted);
        // break up into pages
        let pageSize = 10;
        let statusPageSize = 50;
        let pickListRequests = Math.ceil(orderIds.length / pageSize);
        let statusRequests = updateStatus ? Math.ceil(trackedOrderIds.length / statusPageSize) : 0;
        let pickListMax = pickListRequests / (pickListRequests + statusRequests) * 100;
        let statusMax = statusRequests / (pickListRequests + statusRequests) * 100;
        let merger = new PDFMerger();
        try{
            for (let i = 0; i < orderIds.length; i += pageSize) {
                let page = orderIds.slice(i, i + pageSize);
                // pass ids to api picklist
                let exportLink = (await axios.get(Config.api + `/api/v1/orders/picklist?LogicbrokerKeys=${page.join(',')}&fileType=pdf`)).data.Body;
                // download pdf
                let res = (await axios({url: exportLink, method: 'GET', responseType: 'blob'})).data;
                await merger.add(res);
                if(setProgress){
                    setProgress((pickListMax / orderIds.length) * (i + page.length));
                }
            }
        } catch {
            setState(prev => ({...prev, showError: true, error: `Unexpected error while downloading packing slips. Please try again.`}));
            return;
        }

        let blob = await merger.saveAsBlob();
        // update tracked orders
        let updated = false;
        if(updateStatus){
            let totalCount = trackedOrderIds.length;
            let updatedCount = 0;
            try{
                while(trackedOrderIds.length > 0){
                    let page = trackedOrderIds.slice(0, statusPageSize);
                    trackedOrderIds = trackedOrderIds.slice(statusPageSize);
                    let req = {
                        "Status": "Shipped",
                        "OnlyIncreaseStatus": true,
                        "LogicbrokerKeys": page
                    };
                    await axios.put(Config.api + '/api/v2/orders/status', req);
                    updatedCount += page.length;
                    if(setProgress){
                        setProgress(pickListMax + (statusMax * (updatedCount / totalCount)));
                    }
                }
                updated = true;
            } catch {
                setState(prev => ({...prev, showError: true, error: `Unexpected error while updating status. ${updatedCount} orders were updated. Please try again.`}));
            }
        }
        // save blob to disk
        FileSaver.saveAs(blob, state.fileName);
        if(updated){
            setState(prev => ({...prev, showModal: false}));
        }
    };
    const group = (data: any[], keySelector: (val: any) => any): {key: any, values: any[]}[] => {
        return data.reduce((acc: {key: any, values: any[]}[], val: any) => {
            let key = keySelector(val);
            let existing = acc.filter(r => isEqual(r.key, key))[0];
            if(!existing){
                acc.push({key, values: [val]});
            } else {
                existing.values.push(val);
            }
            return acc;
        }, []);
    };
    const sort = (data: any[], fields: string[]) => {
        let sorted = data;
        fields = fields.filter(r => !(!r));
        if(fields.length > 0){
            sorted = data.sort((a, b) => fields.map(r => (a[r] || '').toString().localeCompare((b[r] || '').toString())).filter(r => r !== 0)[0] || 0);
        }
        return sorted;
    };
    const downloadCsv = () => {
        let rows: any[] = [['SupplierSKU', 'PartnerSKU', 'Description', 'Quantity', 'SenderClassCode', 'Arrival Type', 'Requested Delivery']];
        let sorted = sort(filterOrders(state.orders), state.sort);
        let grouped = group(sorted, r => ({
            [supplierSku]: r[supplierSku],
            [partnerSku]: r[partnerSku],
            [description]: r[description],
            [senderClassCode]: r[senderClassCode],
            [arrivalType]: r[arrivalType],
            [requestedDelivery]: r[requestedDelivery]}));
        let selected = grouped.map(r => [
            r.key[supplierSku],
            r.key[partnerSku],
            r.key[description],
            r.values.reduce((a, x) => parseInt(x[quantity]) + a, 0),
            r.key[senderClassCode],
            r.key[arrivalType],
            r.key[requestedDelivery]]);
        rows = [...rows, ...selected];
        let output = Papa.unparse(rows);
        let blob = new Blob([output], { type: "text/csv" });
        FileSaver.saveAs(blob, state.fileName.replace('.pdf', '.csv'));
    };
    const setSort = (index: number, value: string) => {
        let newSort = [...state.sort];
        newSort[index] = value;
        setState(prev => ({...prev, sort: newSort}));
    };

    let orderCount = getUniqueIds(state.orders).length;
    let trackedOrderCount = getUniqueIdsWithTracking(state.orders).length;
    let progress = <div style={{height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><CircularProgress size={80} variant='indeterminate'/></div>;
    return <>
        <DownloadButton {...props} onClick={loadOrders}>Bulk Pick</DownloadButton>
        <Dialog open={state.showModal} maxWidth='md' fullWidth onClose={() => setState(prev => ({...prev, showModal: false}))}>
            <Toolbar className='lbtoolbar'>Bulk Pick</Toolbar>
            <DialogContent style={{height: '100%'}}>
                <Typography style={{marginBottom: '0.5em'}}>Found {orderCount} orders in status Ready to Ship{isFiltered() ? ' matching your filters' : ''}. {trackedOrderCount} of them have auto-generated tracking numbers and will be moved to Shipped once you download the packing slips.</Typography>
                <div style={{display: 'flex',}}>
                    <div className="imagePreview" style={{maxHeight: state.loaded ? '1000px': undefined, minHeight: '150px'}}>
                        {state.loadedOrders && <Image src={state.imageUrl} loadImage={progress} onLoad={() => setState(prev => ({...prev, loaded: true}))}/>}
                    </div>
                    <div style={{paddingLeft: '20px'}}>
                        <Typography style={{fontWeight: 'bold'}}>Filter by</Typography>
                        <div style={{display: 'flex', flexDirection: 'column', marginBottom: '0.5em'}}>
                            <FormControl style={{paddingTop: '1rem'}}>
                                <InputLabel shrink={true} htmlFor='SupplierSKU'>Supplier SKU</InputLabel>
                                <ExtendedSelect displayEmpty style={{width: '26.25rem'}} placeholder='Any' name='SupplierSKU' options={state.skus.map(r => ({label: r, value: r}))} value={state.filterSku} onChange={(v: string[]) => setState(prev => ({...prev, filterSku: v.filter(r => r !== "")}))}/>
                            </FormControl>
                            <FormControl style={{paddingTop: '1rem'}}>
                                <InputLabel shrink={true} htmlFor='PartnerSKU'>Partner SKU</InputLabel>
                                <ExtendedSelect disabled={!state.partnerSkus || state.partnerSkus.length === 0} displayEmpty style={{width: '26.25rem'}} placeholder='Any' name='PartnerSKU' options={state.partnerSkus.map(r => ({label: r, value: r}))} value={state.filterPartnerSku} onChange={(v: string[]) => setState(prev => ({...prev, filterPartnerSku: v.filter(r => r !== "")}))}/>
                            </FormControl>
                            <FormControl style={{paddingTop: '1rem'}}>
                                <InputLabel shrink={true} htmlFor='RequestedShipDate'>Requested Ship Date</InputLabel>
                                <ExtendedSelect displayEmpty placeholder='Any' name='RequestedShipDate' style={{width: '26.25rem'}} options={state.shipDates.map(r => ({label: r, value: r}))} value={state.filterShipDate} onChange={(v: string[]) => setState(prev => ({...prev, filterShipDate: v.filter(r => r !== "")}))}/>
                            </FormControl>
                        </div>
                        <Typography style={{fontWeight: 'bold'}}>Sort by</Typography>
                        <div style={{display: 'flex', flexDirection: 'column'}}>
                            {state.sort.map((r, i) => <Select {...({placeholder: 'Select...'} as any)} style={{width: '26.25rem'}} key={i} value={r} onChange={v => setSort(i, v.target.value as string)}>
                                <MenuItem value="">Select...</MenuItem>
                                {columns.sort().map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </Select>)}
                        </div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <DownloadButton disabled={orderCount === 0} variant='text' onClick={async () => await downloadCsv()}>Download picklist</DownloadButton>
                <DownloadButton disabled={orderCount === 0} variant='text' onClick={async (setProgress) => await downloadPickList(false, setProgress)}>Download preview</DownloadButton>
                <DownloadButton disabled={orderCount === 0} variant='text' color='primary' onClick={async (setProgress) => await downloadPickList(true, setProgress)}>Download and move to shipped</DownloadButton>
            </DialogActions>
        </Dialog>
        <Dialog open={state.showError} onClose={() => {setState(prev => ({...prev, showError: false}))}}>
            <Toolbar className='lbtoolbar'>Error</Toolbar>
            <DialogContent><Typography>{state.error}</Typography></DialogContent>
        </Dialog>
    </>;
};

export default BulkPick;
